import React, { useState, useEffect } from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import { Info, ExpandMore } from '@mui/icons-material';
import { formatDateTime } from '../../utils/misc';
import { CheckCircleIcon, RunTestIcon } from '../../svgUrls';
import HorizontalTabs from '../Common/HorizontalTabs';
import clsx from 'clsx';
import { NodeRunInstance, NodeTestRunInstance, RunInstance } from '../../types/workflow';
import { useExecuteTestRunForNode, useGetNodeTestRunInstance, useGetTestRunInputsForEachNode } from '../../api/nodeApi';
import "../../styles/runTest.css";

interface AgentNodeTestSectionProps {
    nodeId?: string;
    workflowNode: any;
    testRunInputs?: { run_instance: RunInstance, node_run_instances: NodeRunInstance[] }[];
    selectedRunInstanceId?: string;
    setSelectedRunInstanceId?: (value: string) => void;
    refetchTestRunInputs?: (params: { nodeId: string, reset: boolean }, options?: { onSettled: () => void }) => void;
    updateNodeConfigsFromRunInstances?: (nodeId: string, response_config: string) => void;
    isExpanded: boolean;
    onClose: () => void;
}

const AgentNodeTestSection: React.FC<AgentNodeTestSectionProps> = ({
    nodeId = '',
    workflowNode,
    testRunInputs = [],
    selectedRunInstanceId = '',
    setSelectedRunInstanceId,
    refetchTestRunInputs,
    updateNodeConfigsFromRunInstances,
    isExpanded,
    onClose
}) => {
    // Test run state
    const [isTestRunning, setIsTestRunning] = useState(false);
    const [isTestHeaderLoading, setIsTestHeaderLoading] = useState(false);
    const [showTestResult, setShowTestResult] = useState(false);
    const [testRunSuccessful, setTestRunSuccessful] = useState(false);
    const [testRunTimeStamp, setTestRunTimeStamp] = useState('');
    const [selectedTestTab, setSelectedTestTab] = useState('inputs');
    const [testRunResultsInput, setTestRunResultsInput] = useState<Record<string, string>>({});
    const [testRunResultsOutput, setTestRunResultsOutput] = useState<Record<string, string>>({});

    // Test run API hooks
    const { mutate: executeTestRunMutation } = useExecuteTestRunForNode();
    const { refetch: refetchTestRunInstance } = useGetNodeTestRunInstance(nodeId);
    const getTestRunInputsMutation = useGetTestRunInputsForEachNode();

    // Handle test run execution
    const handleTestRun = () => {
        if (!nodeId || !selectedRunInstanceId) return;

        setIsTestRunning(true);
        setShowTestResult(false);

        executeTestRunMutation(
            {
                runInstanceId: selectedRunInstanceId,
                node: workflowNode?.node || {}
            },
            {
                onSuccess: (data) => {
                    setTestRunSuccessful(true);
                    setShowTestResult(true);
                    setTestRunTimeStamp(formatDateTime(new Date().toISOString()));

                    // Process input and output data
                    const inputData: Record<string, string> = {};
                    const outputData: Record<string, string> = {};

                    try {
                        // Parse input data
                        if (data?.input_data) {
                            let parsedInput;
                            try {
                                parsedInput = typeof data.input_data === 'string' ? JSON.parse(data.input_data) : data.input_data;
                            } catch (e) {
                                console.error('Error parsing input data as JSON, using as is:', e);
                                parsedInput = { raw: data.input_data };
                            }

                            Object.entries(parsedInput).forEach(([key, value]) => {
                                try {
                                    inputData[key] = typeof value === 'string' ? value : JSON.stringify(value);
                                } catch (e) {
                                    console.error(`Error stringifying value for key ${key}:`, e);
                                    inputData[key] = String(value);
                                }
                            });
                        } else {
                            console.log('No input data available in response');
                        }

                        // Parse output data
                        if (data?.output_data) {
                            let parsedOutput;
                            try {
                                parsedOutput = typeof data.output_data === 'string' ? JSON.parse(data.output_data) : data.output_data;
                            } catch (e) {
                                console.error('Error parsing output data as JSON, using as is:', e);
                                parsedOutput = { raw: data.output_data };
                            }

                            Object.entries(parsedOutput).forEach(([key, value]) => {
                                try {
                                    outputData[key] = typeof value === 'string' ? value : JSON.stringify(value);
                                } catch (e) {
                                    console.error(`Error stringifying value for key ${key}:`, e);
                                    outputData[key] = String(value);
                                }
                            });
                        } else {
                            console.log('No output data available in response');
                        }
                    } catch (e) {
                        console.error('Error parsing test run data:', e);
                    }

                    setTestRunResultsInput(inputData);
                    setTestRunResultsOutput(outputData);

                    // Refresh test run instance data
                    refetchTestRunInstance();

                    // Automatically switch to outputs tab if test is successful
                    setSelectedTestTab('outputs');
                },
                onError: (error) => {
                    console.error('Test run failed with error:', error);
                    setTestRunSuccessful(false);
                    setShowTestResult(true);
                    setTestRunTimeStamp(formatDateTime(new Date().toISOString()));
                },
                onSettled: () => {
                    setIsTestRunning(false);

                    // Force a re-render by updating state
                    setSelectedTestTab(prev => {
                        return prev;
                    });
                }
            }
        );
    };

    // Refresh test run inputs
    const refreshTestInputs = () => {
        if (!nodeId) return;

        setIsTestHeaderLoading(true);
        if (refetchTestRunInputs) {
            refetchTestRunInputs(
                { nodeId, reset: false },
                {
                    onSettled: () => {
                        setIsTestHeaderLoading(false);
                    }
                }
            );
        } else {
            // Fallback if refetchTestRunInputs is not provided
            getTestRunInputsMutation.mutate(
                { nodeId, reset: false },
                {
                    onSettled: () => {
                        setIsTestHeaderLoading(false);
                    }
                }
            );
        }
    };

    return (
        <div className="mb-4 border rounded-lg overflow-hidden shadow-sm">
            {/* Accordion Header */}
            <div
                className={clsx(
                    "flex justify-between items-center p-3 cursor-pointer hover:bg-gray-50",
                    isExpanded ? "bg-backgroundBlue border-b" : "bg-white"
                )}
                onClick={onClose}
            >
                <div className="flex items-center">
                    <h3 className="text-sm font-medium">Test Agent</h3>
                </div>
                <ExpandMore
                    className={clsx(
                        "transition-transform duration-200 text-gray-500",
                        { "transform rotate-180": isExpanded }
                    )}
                    fontSize="small"
                />
            </div>

            {/* Accordion Content */}
            {isExpanded && (
                <div className="p-3 bg-backgroundBlue">
                    {/* Test Run Header */}
                    <div className="flex items-center justify-center bg-backgroundBlue rounded-lg p-4 text-sm">
                        <div className="flex flex-col items-center gap-3">
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleTestRun}
                                className="!bg-nanonetsBlue !normal-case !text-white hover:!bg-nanonetsBlueHover"
                                startIcon={
                                    isTestRunning ? (
                                        <div className="testRunIconRotate">
                                            <RunTestIcon />
                                        </div>
                                    ) : (
                                        <RunTestIcon />
                                    )
                                }
                                disabled={isTestRunning}
                            >
                                {isTestRunning ? 'Testing...' : 'Test agent'}
                            </Button>
                        </div>
                    </div>
                    <div>
                        {/* Test Results */}
                        {showTestResult && (
                            <div className="mt-4">
                                {testRunSuccessful ? (
                                    <div className="flex items-center gap-2 p-2 bg-[#f1f8f2] rounded text-xs">
                                        <CheckCircleIcon />
                                        <strong>Test ran successfully</strong> {testRunTimeStamp}
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 py-1 px-2 bg-[#fcf2f1] rounded text-xs">
                                        <Info fontSize="small" htmlColor="#db584e" />
                                        <strong>Test failed to run</strong> {testRunTimeStamp}
                                    </div>
                                )}
                                <HorizontalTabs
                                    tabArray={[
                                        { id: "inputs", value: "Inputs" },
                                        { id: "outputs", value: "Outputs" }
                                    ]}
                                    tabValue={selectedTestTab}
                                    setTabValue={setSelectedTestTab}
                                />
                                <div>
                                    {Object.entries(selectedTestTab === 'inputs' ? testRunResultsInput : testRunResultsOutput).length > 0 ? (
                                        Object.entries(selectedTestTab === 'inputs' ? testRunResultsInput : testRunResultsOutput).map(([dataKey, dataValue]) => (
                                            <div className="my-4" key={dataKey}>
                                                <div className="text-2xs text-greyishBlue mb-2">
                                                    {dataKey}
                                                </div>
                                                <div className="p-3 text-xs bg-backgroundGrey max-h-[140px] overflow-auto rounded text-wrap break-words">
                                                    {typeof dataValue === 'string' ? dataValue : JSON.stringify(dataValue)}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="border border-solid border-borderGrey text-xs flex items-center gap-2 mt-2 p-2 rounded">
                                            <Info fontSize="small" htmlColor="#7e86a0" />
                                            No {selectedTestTab} to display
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgentNodeTestSection; 